<template>
  <div class="movements-report ma-5">
    <!-- Filtros -->
    <div class="filters-container mb-4">
      <v-select
        v-model="selectedPeriod"
        :items="periods"
        label="Periodo"
        dense
        outlined
        hide-details
        class="filter-select"
        @change="onPeriodChange"
      />
      <v-menu
        v-if="selectedPeriod === 'Personalizado'"
        v-model="dateMenu"
        :close-on-content-click="false"
        transition="scale-transition"
        offset-y
        min-width="auto"
      >
        <template #activator="{ on, attrs }">
          <v-text-field
            v-model="customDateRangeText"
            label="Rango de fechas"
            prepend-icon="mdi-calendar"
            readonly
            dense
            outlined
            hide-details
            class="date-range-input"
            v-bind="attrs"
            v-on="on"
          />
        </template>
        <v-date-picker
          v-model="customDateRange"
          range
          no-title
          scrollable
          locale="es"
          @change="onCustomDateChange"
        />
      </v-menu>
      <FilterAutocomplete
        v-model="selectedSources"
        :items="sourcesList"
        label="Filtrar por Fuente"
        item-text="name"
        item-value="_id"
        class="filter-select-sources"
      />
      <v-btn color="primary" @click="refreshData">
        <v-icon left small>mdi-refresh</v-icon>
        Actualizar
      </v-btn>
    </div>

    <!-- Resumen de fases -->
    <div class="phases-summary mb-4">
      <div class="summary-header">
        <div class="summary-title">
          <v-icon color="primary" class="mr-2"
            >mdi-chart-timeline-variant</v-icon
          >
          Resumen de Performance
        </div>
        <v-chip color="primary" small outlined>
          {{ periodLabel }}
        </v-chip>
      </div>

      <!-- Barra de progreso de fases -->
      <div class="phases-progress-bar">
        <v-tooltip
          v-for="phase in phasesWithPercentage"
          :key="phase.key"
          bottom
        >
          <template #activator="{ on, attrs }">
            <div
              v-bind="attrs"
              v-on="on"
              class="phase-segment"
              :class="phase.colorClass"
              :style="{ width: phase.percentage + '%' }"
            >
              <span v-if="phase.percentage > 8" class="segment-label">
                {{ phase.total }}
              </span>
            </div>
          </template>
          <span>{{ phase.name }}: {{ phase.total }} movimientos</span>
        </v-tooltip>
      </div>

      <!-- Leyenda de fases -->
      <div class="phases-legend">
        <div v-for="phase in phases" :key="phase.key" class="legend-item">
          <span class="legend-color" :class="phase.colorClass"></span>
          <span class="legend-text">{{ phase.name }}</span>
        </div>
      </div>
    </div>

    <!-- Resumen por fuente -->
    <div v-if="sourcesBreakdown.length > 0" class="sources-breakdown mb-3">
      <span class="breakdown-label">
        <v-icon x-small class="mr-1">mdi-source-branch</v-icon>
        Fuentes actuales:
      </span>
      <span
        v-for="item in sourcesBreakdown"
        :key="item.sourceId"
        class="source-item"
      >
        <span class="source-name">{{ item.name }}</span>
        <span class="source-count">{{ item.count }}</span>
      </span>
    </div>

    <!-- Tabla principal -->
    <v-card class="movements-table-card" elevation="2">
      <v-data-table
        :headers="headers"
        :items="movementsData"
        :items-per-page="10"
        :loading="loading"
        class="movements-table"
        :footer-props="{
          'items-per-page-options': [5, 10, 25],
          'items-per-page-text': 'Filas por página'
        }"
        :no-data-text="
          loading ? 'Cargando datos...' : 'No hay datos disponibles'
        "
      >
        <!-- Header personalizado para período -->
        <template #header.period="{ header }">
          <div class="header-with-icon">
            <v-icon small class="mr-1">mdi-calendar</v-icon>
            {{ header.text }}
          </div>
        </template>

        <!-- Columna de período -->
        <template #item.period="{ item }">
          <div class="period-cell">
            <v-icon small color="grey" class="mr-2">mdi-calendar-range</v-icon>
            <div>
              <div class="period-date">{{ item.period }}</div>
              <div class="period-subtitle">{{ item.periodSubtitle }}</div>
            </div>
          </div>
        </template>

        <!-- Columna de total movimientos -->
        <template #item.totalMovements="{ item }">
          <v-chip color="primary" small class="font-weight-bold">
            {{ item.totalMovements }}
          </v-chip>
        </template>

        <!-- Columnas dinámicas para cada fase -->
        <template v-for="phase in phases" #[`item.${phase.key}`]="{ item }">
          <div :key="phase.key" class="phase-cell">
            <div
              class="phase-value clickable"
              :class="phase.colorClass + '-text'"
              @click="openLeadsModal(item, phase)"
            >
              {{ item[phase.key].count }}
            </div>
            <div
              v-if="
                item.differentials && item.differentials[phase.key] !== null
              "
              class="differential"
              :class="getDifferentialClass(item.differentials[phase.key])"
            >
              {{ formatDifferential(item.differentials[phase.key]) }}
            </div>
          </div>
        </template>

        <!-- Header personalizado para cada fase con totales -->
        <template v-for="phase in phases" #[`header.${phase.key}`]>
          <div :key="phase.key" class="phase-header" :class="phase.colorClass">
            <div class="phase-header-name">{{ phase.name }}</div>
            <v-chip
              x-small
              :color="phase.chipColor"
              dark
              class="phase-header-total"
            >
              {{ getTotalByPhase(phase.key) }}
            </v-chip>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Modal de leads -->
    <ListLeads
      :leads="detail.leads"
      :dialog="detail.dialog"
      :loading="detail.loading"
      @setDialog="value => (detail.dialog = value)"
    />
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { fetchMovementsReportData } from "@/api/reports-api";
import ListLeads from "@/components/lead/components/simpleListLeadsModal/index.vue";
import FilterAutocomplete from "./component/FilterAutocomplete.vue";
import { DEFAULT_PHASES } from "@/constants/phases";

// Colores para las fases del reporte
const PHASE_COLORS = {
  unassigned: { colorClass: "phase-unassigned", chipColor: "#78909C" },
  assigned: { colorClass: "phase-assigned", chipColor: "#9E9E9E" },
  "to-contact": { colorClass: "phase-to-contact", chipColor: "#2196F3" },
  searching: { colorClass: "phase-searching", chipColor: "#00BCD4" },
  tracking: { colorClass: "phase-tracking", chipColor: "#3F51B5" },
  "scheduled-tour": {
    colorClass: "phase-scheduled-tour",
    chipColor: "#9C27B0"
  },
  "finished-tour": { colorClass: "phase-finished-tour", chipColor: "#E91E63" },
  offer: { colorClass: "phase-offer", chipColor: "#FF9800" },
  downpayment: { colorClass: "phase-downpayment", chipColor: "#FF5722" },
  contract: { colorClass: "phase-contract", chipColor: "#795548" },
  "closing-trade": { colorClass: "phase-closing-trade", chipColor: "#607D8B" },
  finished: { colorClass: "phase-finished", chipColor: "#4CAF50" },
  discarded: { colorClass: "phase-discarded", chipColor: "#F44336" }
};

export default {
  name: "MovementsReport",
  components: {
    ListLeads,
    FilterAutocomplete
  },
  data() {
    return {
      selectedPeriod: "Semana pasada",
      periods: [
        "Hoy",
        "Ayer",
        "Semana pasada",
        "Últimos 15 días",
        "Mes pasado",
        "Últimos 90 dias",
        "Este año",
        "Año pasado",
        "Personalizado"
      ],
      dateMenu: false,
      customDateRange: [],
      loading: false,
      phases: [], // Se genera dinámicamente desde el backend
      movementsData: [],
      summary: {
        total: 0,
        phases: {}
      },
      grouping: "week",
      detail: {
        leads: [],
        dialog: false,
        loading: false
      },
      selectedSources: [],
      sourcesList: []
    };
  },
  computed: {
    ...mapState({
      leadsListByContact: state => state.reports.leadListByComertialReport,
      filters: state => state.leads.filter.filters
    }),
    headers() {
      const baseHeaders = [
        { text: "Período", value: "period", sortable: false, width: "160px" },
        {
          text: "Total",
          value: "totalMovements",
          sortable: true,
          align: "center",
          width: "80px"
        }
      ];

      const phaseHeaders = this.phases.map(phase => ({
        text: phase.name,
        value: phase.key,
        sortable: false,
        align: "center",
        width: "120px",
        class: phase.colorClass + "-header"
      }));

      return [...baseHeaders, ...phaseHeaders];
    },
    phasesWithPercentage() {
      const total = this.phases.reduce(
        (sum, phase) => sum + this.getTotalByPhase(phase.key),
        0
      );
      return this.phases.map(phase => ({
        ...phase,
        total: this.getTotalByPhase(phase.key),
        percentage:
          total > 0 ? (this.getTotalByPhase(phase.key) / total) * 100 : 0
      }));
    },
    customDateRangeText() {
      if (this.customDateRange.length === 2) {
        const [start, end] = [...this.customDateRange].sort();
        return `${this.formatDate(start)} - ${this.formatDate(end)}`;
      }
      return "Selecciona un rango";
    },
    periodLabel() {
      if (
        this.selectedPeriod === "Personalizado" &&
        this.customDateRange.length === 2
      ) {
        return this.customDateRangeText;
      }
      return this.selectedPeriod;
    },
    sourcesBreakdown() {
      const sourceCounts = {};

      // Recorrer todos los datos de movimientos y contar leads por fuente
      this.movementsData.forEach(row => {
        this.phases.forEach(phase => {
          const leads = row[phase.key]?.leads || [];
          leads.forEach(lead => {
            const sourceId = lead.source;
            if (sourceId) {
              if (!sourceCounts[sourceId]) {
                sourceCounts[sourceId] = 0;
              }
              sourceCounts[sourceId]++;
            }
          });
        });
      });

      // Convertir a array y agregar nombres
      return Object.entries(sourceCounts)
        .map(([sourceId, count]) => ({
          sourceId,
          name: this.getSourceName(sourceId),
          count
        }))
        .sort((a, b) => b.count - a.count); // Ordenar por cantidad desc
    }
  },
  watch: {
    selectedSources() {
      this.fetchData();
    },
    filters: {
      handler() {
        this.loadSourcesList();
      },
      deep: true
    }
  },
  async mounted() {
    await this.fetchGetLeadsFilters();
    this.loadSourcesList();
    this.fetchData();
  },
  methods: {
    ...mapActions({
      getListLeads: "reports/fetchListLeadsByDetailReport",
      fetchGetLeadsFilters: "leads/filter/fetchGetLeadsFilters"
    }),
    getDateRangeFromPeriod(period) {
      // Caso personalizado: usar las fechas seleccionadas (rango real)
      if (period === "Personalizado") {
        if (this.customDateRange.length === 2) {
          const [start, end] = [...this.customDateRange].sort();
          // Agregar T00:00:00 para evitar problemas de zona horaria
          const dateFrom = new Date(start + "T00:00:00");
          const dateTo = new Date(end + "T23:59:59.999");
          return {
            dateFrom: dateFrom.toISOString(),
            dateTo: dateTo.toISOString()
          };
        }
        // Si no hay rango seleccionado, retornar última semana por defecto
        return this.getDateRangeFromPeriod("Semana pasada");
      }

      // Para períodos predefinidos: dateFrom y dateTo forman un rango real
      const now = new Date();
      let dateFrom = new Date(now);
      let dateTo = new Date(now);
      dateFrom.setHours(0, 0, 0, 0);
      dateTo.setHours(23, 59, 59, 999);

      switch (period) {
        case "Hoy":
          // dateFrom y dateTo ya están en la fecha actual
          break;
        case "Ayer":
          dateFrom.setDate(dateFrom.getDate() - 2);
          dateTo.setDate(dateTo.getDate() - 1);
          break;
        case "Semana pasada": {
          const dow = now.getDay(); // 0=Dom, 1=Lun, ..., 6=Sab
          const daysSinceMonday = dow === 0 ? 6 : dow - 1;
          dateFrom.setDate(now.getDate() - daysSinceMonday - 7); // lunes semana pasada
          dateTo.setDate(now.getDate() - daysSinceMonday - 1); // domingo semana pasada
          break;
        }
        case "Últimos 15 días":
          dateFrom.setDate(dateFrom.getDate() - 14);
          break;
        case "Mes pasado":
          dateFrom = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          dateFrom.setHours(0, 0, 0, 0);
          dateTo = new Date(now.getFullYear(), now.getMonth(), 0);
          dateTo.setHours(23, 59, 59, 999);
          break;
        case "Últimos 90 dias":
          dateFrom = new Date(now.getFullYear(), now.getMonth() - 3, 1);
          dateFrom.setHours(0, 0, 0, 0);
          dateTo = new Date(now.getFullYear(), now.getMonth(), 0);
          dateTo.setHours(23, 59, 59, 999);
          break;
        case "Este año":
          dateFrom = new Date(now.getFullYear(), 0, 1);
          dateFrom.setHours(0, 0, 0, 0);
          dateTo = new Date(now.getFullYear(), 11, 31);
          dateTo.setHours(23, 59, 59, 999);
          break;
        case "Año pasado":
          dateFrom = new Date(now.getFullYear() - 1, 0, 1);
          dateFrom.setHours(0, 0, 0, 0);
          dateTo = new Date(now.getFullYear() - 1, 11, 31);
          dateTo.setHours(23, 59, 59, 999);
          break;
        default:
          dateFrom.setDate(dateFrom.getDate() - 6);
      }

      return {
        dateFrom: this.toLocalISOString(dateFrom),
        dateTo: this.toLocalISOString(dateTo)
      };
    },
    toLocalISOString(date) {
      const pad = (n, len = 2) => String(n).padStart(len, "0");
      return (
        `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
          date.getDate()
        )}` +
        `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
          date.getSeconds()
        )}` +
        `.${pad(date.getMilliseconds(), 3)}`
      );
    },
    formatDate(dateStr) {
      const date = new Date(dateStr + "T00:00:00");
      return date.toLocaleDateString("es-MX", {
        day: "2-digit",
        month: "short",
        year: "numeric"
      });
    },
    onPeriodChange(value) {
      if (value === "Personalizado") {
        this.dateMenu = true;
      } else {
        this.customDateRange = [];
        this.fetchData();
      }
    },
    onCustomDateChange() {
      if (this.customDateRange.length === 2) {
        this.dateMenu = false;
        this.fetchData();
      }
    },
    loadSourcesList() {
      if (!this.filters?.sources) return;
      this.sourcesList = this.filters.sources.map(item => item.source);
    },
    async fetchData() {
      this.loading = true;
      try {
        const { dateFrom, dateTo } = this.getDateRangeFromPeriod(
          this.selectedPeriod
        );
        const params = {
          dateFrom,
          dateTo
        };
        if (this.selectedSources.length > 0) {
          params.sources = this.selectedSources;
        }
        const response = await fetchMovementsReportData(params);

        const reportData = response?.data?.data || response?.data;
        if (reportData) {
          const { summary, groups, grouping } = reportData;
          this.summary = summary || { total: 0, phases: {} };
          this.grouping = grouping || "week";
          // Generar fases dinámicamente desde el summary y todos los grupos
          this.phases = this.buildPhasesFromSummary(
            summary?.phases || {},
            groups || []
          );
          this.movementsData = this.mapGroupsData(groups);
        }
      } catch {
        this.loading = false;
      } finally {
        this.loading = false;
      }
    },
    buildPhasesFromSummary(phasesObj, groups) {
      // Recolectar fases del summary y de todos los grupos
      const phaseKeys = new Set(Object.keys(phasesObj));
      groups.forEach(group => {
        (group.phases || []).forEach(p => phaseKeys.add(p.phase));
      });

      // Ordenar las fases según el orden definido en DEFAULT_PHASES
      // Solo incluir las fases que vienen del backend
      return DEFAULT_PHASES.filter(phase => phaseKeys.has(phase.value)).map(
        phase => {
          const colors = PHASE_COLORS[phase.value] || {
            colorClass: "phase-default",
            chipColor: "#9E9E9E"
          };

          return {
            key: phase.value,
            name: phase.text,
            colorClass: colors.colorClass,
            chipColor: colors.chipColor
          };
        }
      );
    },
    mapGroupsData(groups) {
      if (!groups || !Array.isArray(groups)) return [];

      const mappedGroups = groups.map((group, index) => {
        const row = {
          period: group.label,
          periodSubtitle: this.getGroupSubtitle(index, group.start, group.end),
          totalMovements: group.total,
          groupIndex: index,
          differentials: {},
          // Guardar fechas para el filtro del detalle
          startDate: group.start,
          endDate: group.end
        };

        // Inicializar todas las fases con valores por defecto
        this.phases.forEach(phase => {
          row[phase.key] = { count: 0, leads: [] };
        });

        // Mapear las fases del backend
        if (group.phases && Array.isArray(group.phases)) {
          group.phases.forEach(phaseData => {
            const phaseKey = phaseData.phase;
            if (row[phaseKey] !== undefined) {
              row[phaseKey] = {
                count: phaseData.count,
                leads: phaseData.leads || []
              };
            }
          });
        }

        return row;
      });

      // Calcular diferenciales (variación %) comparando con el grupo siguiente (anterior en tiempo)
      mappedGroups.forEach((row, index) => {
        const previousGroup = mappedGroups[index + 1];
        this.phases.forEach(phase => {
          if (previousGroup) {
            const current = row[phase.key].count;
            const previous = previousGroup[phase.key].count;
            if (previous === 0 && current > 0) {
              row.differentials[phase.key] = "Nuevo";
            } else if (previous === 0 && current === 0) {
              row.differentials[phase.key] = 0;
            } else {
              row.differentials[phase.key] =
                Math.round(((current - previous) / previous) * 100 * 10) / 10;
            }
          } else {
            row.differentials[phase.key] = null;
          }
        });
      });

      return mappedGroups;
    },
    getGroupSubtitle(index, startDate, endDate) {
      if (this.grouping === "none") {
        return "Período seleccionado";
      }
      if (this.grouping === "day" && startDate) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const groupDate = new Date(startDate + "T00:00:00");
        const diffDays = Math.round(
          (today - groupDate) / (1000 * 60 * 60 * 24)
        );
        if (diffDays === 0) return "Hoy";
        if (diffDays === 1) return "Ayer";
        return `Hace ${diffDays} días`;
      }
      if (this.grouping === "month" && endDate) {
        const groupDate = new Date(endDate + "T00:00:00");
        const now = new Date();
        const monthsDiff =
          (now.getFullYear() - groupDate.getFullYear()) * 12 +
          (now.getMonth() - groupDate.getMonth());
        if (monthsDiff === 0) return "Mes actual";
        if (monthsDiff === 1) return "Mes pasado";
        return `Hace ${monthsDiff} meses`;
      }
      // grouping === "week": comparar fechas reales contra la semana actual
      if (startDate) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const dow = today.getDay();
        const daysSinceMonday = dow === 0 ? 6 : dow - 1;
        const currentWeekMonday = new Date(today);
        currentWeekMonday.setDate(today.getDate() - daysSinceMonday);

        const groupStart = new Date(startDate + "T00:00:00");
        const diffDays = Math.round(
          (currentWeekMonday - groupStart) / (1000 * 60 * 60 * 24)
        );

        if (diffDays === 0) return "Semana actual";
        if (diffDays === 7) return "Semana pasada";
        return `Hace ${Math.round(diffDays / 7)} semanas`;
      }
      if (index === 0) return "Semana actual";
      if (index === 1) return "Semana pasada";
      return `Hace ${index} semanas`;
    },
    refreshData() {
      this.fetchData();
    },
    getTotalByPhase(phaseKey) {
      return this.movementsData.reduce(
        (sum, row) => sum + (row[phaseKey]?.count || 0),
        0
      );
    },
    getDifferentialClass(differential) {
      if (differential === "Nuevo") return "differential-positive";
      if (differential > 0) return "differential-positive";
      if (differential < 0) return "differential-negative";
      return "differential-neutral";
    },
    formatDifferential(differential) {
      if (differential === "Nuevo") return "Nuevo";
      if (differential > 0) return `+${differential}%`;
      if (differential === 0) return "0%";
      return `${differential}%`;
    },
    getSourceName(sourceId) {
      const source = this.sourcesList.find(s => s._id === sourceId);
      return source?.name || sourceId;
    },
    async openLeadsModal(item, phase) {
      const phaseData = item[phase.key];
      const leads = phaseData?.leads || [];
      if (leads.length === 0) return;

      // Extraer solo los _id de los objetos de leads
      const leadIds = leads.map(lead => lead._id);

      this.detail.loading = true;
      this.detail.dialog = true;

      await this.getListLeads({
        leadIds,
        typeReport: "movements"
      });

      this.detail.leads = this.leadsListByContact;
      this.detail.loading = false;
    }
  }
};
</script>

<style scoped>
.movements-report {
  font-family: "Roboto", sans-serif;
}

/* Filtros */
.filters-container {
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
}

.filter-select {
  max-width: 200px;
  flex-shrink: 0;
}

.date-range-input {
  max-width: 280px;
  flex-shrink: 0;
}

.filter-select-sources {
  max-width: 300px;
  min-width: 200px;
  flex-shrink: 0;
}

/* Resumen de fases */
.phases-summary {
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%);
  border-radius: 12px;
  padding: 1.5rem;
}

.summary-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.summary-title {
  font-size: 1.1rem;
  font-weight: 500;
  color: #333;
  display: flex;
  align-items: center;
}

.phases-progress-bar {
  display: flex;
  height: 32px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
}

.phase-segment {
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  min-width: 2px;
}

.phase-segment:hover {
  filter: brightness(1.1);
  transform: scaleY(1.1);
}

.segment-label {
  color: white;
  font-size: 11px;
  font-weight: 600;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.phases-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 1rem;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.legend-color {
  width: 12px;
  height: 12px;
  border-radius: 3px;
}

.legend-text {
  font-size: 12px;
  color: #666;
}

/* Colores de fases - background */
.phase-unassigned {
  background-color: #78909c;
}
.phase-assigned {
  background-color: #9e9e9e;
}
.phase-to-contact {
  background-color: #2196f3;
}
.phase-searching {
  background-color: #00bcd4;
}
.phase-tracking {
  background-color: #3f51b5;
}
.phase-scheduled-tour {
  background-color: #9c27b0;
}
.phase-finished-tour {
  background-color: #e91e63;
}
.phase-offer {
  background-color: #ff9800;
}
.phase-downpayment {
  background-color: #ff5722;
}
.phase-contract {
  background-color: #795548;
}
.phase-closing-trade {
  background-color: #607d8b;
}
.phase-finished {
  background-color: #4caf50;
}
.phase-discarded {
  background-color: #262d3c;
}
.phase-default {
  background-color: #9e9e9e;
}

/* Tabla */
.movements-table-card {
  border-radius: 12px;
  overflow: hidden;
}

.movements-table >>> .v-data-table-header th {
  background-color: #f8f9fa !important;
  font-weight: 600;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  padding: 12px 8px !important;
  border-bottom: 2px solid #e0e0e0 !important;
}

.period-cell {
  display: flex;
  align-items: center;
}

.period-date {
  font-weight: 500;
  color: #333;
}

.period-subtitle {
  font-size: 11px;
  color: #888;
}

.phase-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 8px 4px;
}

.phase-value {
  font-size: 16px;
  font-weight: 600;
}

.phase-value.clickable {
  cursor: pointer;
  transition: all 0.2s ease;
}

.phase-value.clickable:hover {
  transform: scale(1.1);
  text-decoration: underline;
}

.phase-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px 12px;
  border-radius: 6px;
  min-width: 100px;
}

.phase-header-name {
  font-size: 10px;
  font-weight: 700;
  white-space: nowrap;
  color: white;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.phase-header-total {
  font-size: 11px;
  font-weight: 700;
  min-width: 28px;
  height: 22px;
  background-color: rgba(255, 255, 255, 0.25) !important;
  color: white !important;
}

/* Diferenciales */
.differential {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 4px;
}

.differential-positive {
  color: #2e7d32;
  background-color: rgba(76, 175, 80, 0.15);
}

.differential-negative {
  color: #c62828;
  background-color: rgba(244, 67, 54, 0.15);
}

.differential-neutral {
  color: #757575;
  background-color: rgba(158, 158, 158, 0.15);
}

.header-with-icon {
  display: flex;
  align-items: center;
}

/* Resumen por fuente */
.sources-breakdown {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 10px 14px;
  background-color: #fafafa;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
}

.breakdown-label {
  font-size: 12px;
  color: #666;
  font-weight: 500;
  display: flex;
  align-items: center;
}

.source-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
}

.source-name {
  color: #555;
}

.source-count {
  background-color: #e3f2fd;
  color: #1565c0;
  padding: 2px 6px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 11px;
}
</style>
